/**
 * Converts a string into a URL-friendly slug.
 * - Trims whitespace
 * - Converts to lowercase
 * - Replaces accented characters with unaccented equivalents
 * - Replaces spaces and special characters with dashes
 *
 * @param {string} str - The input string to convert into a slug.
 * @returns {string} - The resulting slug.
 */
export function stringToSlug(str: string) {
	str = str.replace(/^\s+|\s+$/g, '');
	str = str.toLowerCase();

	// remove accents
	const from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;';
	const to = 'aaaaeeeeiiiioooouuuunc------';
	for (let i = 0, l = from.length; i < l; i++) {
		str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
	}

	str = str
		.replace(/[^a-z0-9 -]/g, '') // remove invalid chars
		.replace(/\s+/g, '-') // collapse whitespace and replace with "-"
		.replace(/-+/g, '-'); // collapse dashes

	return str;
}

/**
 * Formats a given date into `MM-DD-YYYY` format.
 * - Accepts either a `Date` object or a date string.
 * - Throws an error if the input date is invalid.
 *
 * @param {Date | string} [input=new Date()] - The input date to format. Defaults to the current date.
 * @throws {Error} If the input date is invalid.
 * @returns {string} - The formatted date in `MM-DD-YYYY` format.
 */
export function formatDate(input: Date | string = new Date()): string {
	const date = typeof input === 'string' ? new Date(input) : input;

	if (isNaN(date.getTime())) {
		throw new Error('Invalid date format');
	}

	return [
		String(date.getMonth() + 1).padStart(2, '0'),
		String(date.getDate()).padStart(2, '0'),
		date.getFullYear()
	].join('-');
}

/**
 * Recursively retrieves the values of a specified key from an object.
 * - Searches the object and all nested objects for the specified key.
 *
 * @param {any} obj - The input object to search.
 * @param {string} compareKey - The key to look for in the object.
 * @returns {any[]} - An array of values corresponding to the specified key.
 */
export const recursivelyRetrieveObjectKeys = (obj: any, compareKey: string[]) => {
	if	(typeof obj !== 'object' || obj === null) return [];
	const result: any[] = [];

	function search(obj: any) {
		if (typeof obj !== 'object' || obj === null) return;

		for (const key in obj) {
			if (compareKey.includes(key)) {
				result.push(obj[key]);
			}
			search(obj[key]);
		}
	}

	search(obj);
	return result;
}
